import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../service/caller';
import { CButton, CModal, CModalBody, CModalHeader, CModalTitle, CModalFooter } from '@coreui/react';
import { BsArrowRight } from 'react-icons/bs';
import { toast } from 'react-toastify';
import { format } from "date-fns";

const EditAppointmentModal = ({ visible, onClose, appointmentId }) => {
    const navigate = useNavigate();
    const [appointmentData, setAppointmentData] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [hospitalId, setHospitalId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [specificDoctor, setSpecificDoctor] = useState(false);
    const [patientData, setPatientData] = useState(null);
    const [hospitalData, setHospitalData] = useState(null);
    const [doctors, setDoctors] = useState([]);

    // Fetch departments
    useEffect(() => {
        const fetchDepartments = async () => {
            try {
                const response = await api.get(`/api/departments`, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                    }
                });
                setDepartments(response.data.data);
            } catch (error) {
                console.error('Error fetching departments:', error);
                setError('Failed to load departments');
            }
        };
        
        if (visible) {
            fetchDepartments();
        }
    }, [visible]);

    // Fetch appointment data
    useEffect(() => {
        const fetchAppointmentData = async () => {
            if (!appointmentId) return;
            
            setLoading(true);
            setError('');
            
            try {
                // Fetch appointment data
                const appointmentResponse = await api.get(`/clinic/getappointment/${appointmentId}`);
                const appointment = appointmentResponse.data.appointment;
                // Format date for the date input (YYYY-MM-DD)
                if (appointment.AppointmentDate) {
                    const dateObj = new Date(appointment.AppointmentDate);
                    const formattedDate = dateObj.toISOString().split('T')[0];
                    appointment.AppointmentDate = formattedDate;
                }
                try {
                    const response = await api.get(`/api/getDoctorsByDepartment/${appointment.department}`);
                    if (response.data && response.data.data) {
                        setDoctors(response.data.data);
                    } 
                } catch (err) {
                    console.error('Erreur détaillée:', err);
                }
                
                setAppointmentData(appointment);
                setHospitalId(appointment.hospitalId);
                setSpecificDoctor(appointment.doctorId ? true : false);
                
                // Fetch patient data
                try {
                    const patientResponse = await api.get(`/clinic/getpatient/${appointment.patientId._id || appointment.patientId}`);
                    setPatientData(patientResponse.data);
                } catch (patientError) {
                    console.error('Error fetching patient data:', patientError);
                }
                
                // Fetch hospital data
                try {
                    const hospitalResponse = await api.get(`/api/gethospital/${appointment.hospitalId}`);
                    setHospitalData(hospitalResponse.data);
                } catch (hospitalError) {
                    console.error('Error fetching hospital data:', hospitalError);
                }
                
            } catch (error) {
                console.error('Error fetching appointment data:', error);
                setError('Failed to load appointment data');
            } finally {
                setLoading(false);
            }
        };
        
        if (appointmentId && visible) {
            fetchAppointmentData();
        }
    }, [appointmentId, visible]);

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setAppointmentData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Submit form
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!appointmentData) return;
        
        setLoading(true);
        setError('');
        
        try {
            const response = await api.put(`/clinic/updateappointments/${appointmentId}`, appointmentData);
            
            if (response.status === 200) {
                toast.success('Appointment updated successfully');
                // Close modal and refresh the appointment list
                // onClose();
                // window.location.reload(); // Simple refresh to see changes
            } else {
                throw new Error('Failed to update appointment');
            }
        } catch (error) {
            console.error('Error updating appointment:', error);
            setError('Failed to update appointment. Please try again.');
            toast.error('Failed to update appointment');
        } finally {
            setLoading(false);
        }
    };

    return (
        <CModal
            className="edit-appointment-modal newregistermodal"
            alignment="center"
            scrollable
            size="lg"
            visible={visible}
            onClose={onClose}
            aria-labelledby="EditAppointmentModal"
        >
            <CModalHeader closeButton>
                <CModalTitle id="EditAppointmentModal">Edit Appointment</CModalTitle>
            </CModalHeader>
            
            <CModalBody className="p-4">
                { !appointmentData ? (
                    <div className="text-center">No appointment data found</div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        {patientData && (
                            <div className="form-group">
                                <label htmlFor="patient_Name">Patient Name</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={`${patientData.firstname || ''} ${patientData.lastname || ''}`}
                                    disabled
                                />
                            </div>
                        )}

                        {hospitalData && (
                            <div className="form-group">
                                <label htmlFor="hospitalId">Hospital</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={hospitalData.hospital_name || ''}
                                    disabled
                                />
                            </div>
                        )}

                        <div className="form-group">
                            <label htmlFor="motif">Motif</label>
                            <input
                                type="text"
                                className="form-control"
                                name="motif"
                                value={appointmentData.motif || ''}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="department">Department</label>
                            <select
                                className="form-control"
                                name="department"
                                value={appointmentData.department || ''}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select Department</option>
                                {departments.map((department) => (
                                    <option key={department._id} value={department._id}>
                                        {department.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="price">Price</label>
                            <input
                                type="number"
                                className="form-control"
                                name="price"
                                value={appointmentData.price || ''}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="appointmentDate">Appointment Date</label>
                            <input
                                type="date"
                                className="form-control"
                                name="appointmentDate"
                                value={format(new Date(appointmentData.appointmentDate), "yyyy-MM-dd") || ''}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="appointmentTime">Appointment Time</label>
                            <input
                                type="time"
                                className="form-control"
                                name="appointmentTime"
                                value={appointmentData.appointmentTime || ''}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>See specific doctor?</label>
                            <div className="d-flex gap-3 mt-2">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="specificDoctor"
                                        id="specificDoctorYes"
                                        onChange={() => setSpecificDoctor(true)}
                                        checked={specificDoctor === true}
                                    />
                                    <label className="form-check-label" htmlFor="specificDoctorYes">
                                        Yes, I want to see a specific doctor
                                    </label>
                                </div>
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="specificDoctor"
                                        id="specificDoctorNo"
                                        onChange={() => setSpecificDoctor(false)}
                                        checked={specificDoctor === false}
                                    />
                                    <label className="form-check-label" htmlFor="specificDoctorNo">
                                        No, I don't mind
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Show doctor selection if specificDoctor is true */}
                        {specificDoctor && (
                            <div className="form-group">
                                <label htmlFor="doctorId">Select Doctor</label>
                                <select
                                    className="form-control"
                                    name="doctorId"
                                    value={appointmentData.doctorId || ''}
                                    onChange={handleChange}
                                    required={specificDoctor}
                                >
                                    <option value="">Select Doctor</option>
                                    {doctors.map((department) => (
                                        <option key={department._id} value={department._id}>
                                            {department.firstname+" "+department.lastname }
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </form>
                )}
            </CModalBody>
            
            <CModalFooter>
                <CButton color="secondary" onClick={onClose}>
                    Cancel
                </CButton>
                <CButton 
                    color="primary" 
                    onClick={handleSubmit}
                    disabled={loading || !appointmentData}
                >
                    {loading ? 'Saving...' : 'Save Changes'} &nbsp; <BsArrowRight />
                </CButton>
            </CModalFooter>
        </CModal>
    );
};

export default EditAppointmentModal;